<template>
  <div>
    <div v-if="width > mobileWindowWidth">
      <ag-grid-vue
        class="ag-theme-material h-100"
        :gridOptions="gridOptions"
        :columnDefs="columnDefs"
        :rowData="sortedDirections"
      />
    </div>
    <div v-else>
      <b-card v-for="(direction, index) of sortedDirections" :key="index" class="mt-3">
        <DetailDirectionInfo :direction="getDirectionView(direction)" />
      </b-card>
    </div>
    <b-modal
      v-if="partner"
      :id="`detailDirectionModal-${partner.id}`"
      size="lg"
      :hide-footer="true"
      title="Информация о направлении"
    >
      <h5>Выбранное направление</h5>
      <b-card>
        <DetailDirectionInfo :direction="selectedRow" />
      </b-card>
      <template v-if="sameRows.length">
        <h5 class="mt-2">Похожие направления</h5>
        <b-card class="mb-3" v-for="(direction, index) of sameRows" :key="index">
          <DetailDirectionInfo :direction="direction" />
        </b-card>
      </template>
    </b-modal>
  </div>
</template>

<script>
import _ from 'lodash'
import { AgGridVue } from 'ag-grid-vue'
import AgGridFactory from '@/components/helpers/factory/agGrid'
import { studyingForms } from '@/config/studying-forms'
import DetailDirectionInfo from '@/components/common/university/DetailDirectionInfo.vue'

export default {
  name: 'SelectedUniversity',
  components: {
    AgGridVue,
    DetailDirectionInfo
  },
  inject: ['windowWidth', 'currentYear'],
  props: {
    partner: {
      type: Object,
      default() {
        return null
      }
    },
    filter: {
      type: Object,
      default() {
        return {}
      }
    }
  },
  watch: {
    year() {
      this.setColumnNames()
    }
  },
  computed: {
    width() {
      return this.windowWidth()
    },
    year() {
      return this.currentYear()
    },
    sortedDirections() {
      return this.partner && this.partner.directions
        ? _.sortBy(this.partner.directions
          .map((d) => ({
            ...d,
            codeTitle: this.getDirectionTitle(d.educationDirection)
          })), ['educationDirection.code', 'studyingForm'])
        : []
    }
  },
  data() {
    return {
      mobileWindowWidth: 700,
      selectedRow: null,
      sameRows: [],
      gridOptions: AgGridFactory.getGridOptionsDefault({
        defaultColDef: {
          sortable: true,
          filter: false,
          resizable: true
        },
        rowStyle: { cursor: 'pointer' },
        enableBrowserTooltips: true,
        domLayout: 'autoHeight',
        pagination: false,
        onRowClicked: (params) => {
          this.selectedRow = this.getDirectionView(params.data)
          this.sameRows = this.sortedDirections
            .filter((d) => d.id !== params.data.id
              && d.educationDirection.id === params.data.educationDirection.id)
            .map((d) => this.getDirectionView(d))
          this.$bvModal.show(`detailDirectionModal-${this.partner.id}`)
        }
      }),
      columnDefs: [
        {
          headerName: 'Направление',
          field: 'codeTitle',
          tooltipField: 'codeTitle',
          tooltipShowDelay: 0,
          filter: true,
          minWidth: 250,
          valueFormatter: (params) => this.getDirectionTitle(params.data.educationDirection)
        },
        {
          headerName: 'Тип',
          field: 'studyingForm',
          valueFormatter: (params) => this.getDirectionType(params.value),
          suppressSizeToFit: true,
          width: 120
        },
        {
          headerName: 'Мест',
          field: 'quota',
          suppressSizeToFit: true,
          width: 120
        },
        {
          headerName: 'Прох. балл',
          field: 'minimalMark',
          suppressSizeToFit: true,
          width: 120,
          cellRenderer: (params) => this.getDirectionMinimalMark(params.data)
        },
        {
          headerName: 'Доп. балл',
          field: 'addMark',
          suppressSizeToFit: true,
          width: 120,
          valueFormatter: (params) => this.getDirectionBenefit(params.data)
        }
      ]
    }
  },
  created() {
    this.setColumnNames()
  },
  methods: {
    getDirectionTitle(value) {
      return `${value.code} ${value.title}`
    },
    getDirectionType(value) {
      const form = studyingForms.find((sf) => sf.value === value)
      return form ? form.text : '?'
    },
    getDirectionMinimalMark(data) {
      if (!this.filter.subject || !this.filter.subject.length || data.minimalMark === '-') {
        return data.minimalMark
      }
      const subjectFilter = this.filter.subject.map((s) => ({
        id: s.subject.id,
        addMark: Number(s.addMark)
      }))
      const directionSubjects = data.egeSubjects
        .map((es) => ({
          id: es.id,
          order: es.directionUniversitySubject.order
        }))
      const summarySubjects = []
      for (const subject of subjectFilter) {
        const subjectInfo = directionSubjects.find((s) => s.id === subject.id)
        if (subjectInfo) {
          const summarySubjectIndex = summarySubjects
            .findIndex((s) => s.order === subjectInfo.order)
          if (summarySubjectIndex > -1) {
            summarySubjects[summarySubjectIndex].addMark = Math
              .max(summarySubjects[summarySubjectIndex].addMark, subject.addMark)
          } else {
            summarySubjects.push({
              order: subjectInfo.order,
              addMark: subject.addMark
            })
          }
        }
      }
      const benefit = this.getDirectionBenefit(data)
      const summaryValue = summarySubjects.map((s) => s.addMark)
        .reduce((prev, curr) => prev + curr) + (benefit !== '-' ? benefit : 0)
      const icon = summaryValue >= data.minimalMark
        ? '<em v-b-tooltip.hover style="color: green" '
        + 'title="Вам хватило бы баллов, чтобы пройти в прошлом году" '
        + 'class="far fa-check-circle" />'
        : '<em v-b-tooltip.hover style="color: red" '
        + 'title="Указанный балл ниже проходного за прошлый год" '
        + 'class="far fa-question-circle" />'
      return `${data.minimalMark} ${icon}`
    },
    getDirectionBenefit(data) {
      if (!this.filter.olympiad) {
        return '-'
      }
      const filterOlympiads = this.filter.olympiad.map((o) => o.id)
      const olympiads = data.recomOlympiads
        .filter((o) => filterOlympiads.includes(o.id))
      return olympiads.length ? olympiads.map((o) => o.olympiadDirection.addMark)
        .reduce((prev, curr) => prev + curr) : '-'
    },
    getDirectionHistory(data) {
      return data.directionMarkHistories
        .sort((y1, y2) => y2.year - y1.year).slice(0, 2)
    },
    getDirectionSubjects(subjects) {
      const sortedSubjects = _.sortBy(subjects, 'directionUniversitySubject.order')
      const groupedSubjects = _.groupBy(sortedSubjects, 'directionUniversitySubject.order')
      return Object.values(groupedSubjects).map((subjectGroup) => subjectGroup
        .map((s) => `${s.name}(${s.directionUniversitySubject.minimalMark})`)
        .join(' / ')).join(', ')
    },
    getDirectionView(direction) {
      return {
        title: this.getDirectionTitle(direction.educationDirection),
        type: this.getDirectionType(direction.studyingForm),
        ege: this.getDirectionSubjects(direction.egeSubjects),
        benefit: this.getDirectionBenefit(direction),
        history: this.getDirectionHistory(direction)
      }
    },
    setColumnNames() {
      const columnMarkIndex = this.columnDefs.findIndex((c) => c.field === 'minimalMark')
      const columnQuotaIndex = this.columnDefs.findIndex((c) => c.field === 'quota')
      if (columnQuotaIndex > -1) {
        this.columnDefs[columnQuotaIndex].headerName = `Мест ${this.partner.quotaYear || ''}`
      }
      if (columnMarkIndex > -1) {
        this.columnDefs[columnMarkIndex].headerName = `Балл ${this.year ? this.year - 1 : ''}`
      }
    }
  }
}
</script>

<style scoped>

</style>
